/**
 * Toast context (PHASE1 §7.2). A small stack of transient notifications with an
 * optional action button (used for the inbox 5s "Rückgängig" undo). Toasts
 * auto-dismiss after `duration` ms unless `duration` is 0.
 */
import { createContext, use, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  variant?: ToastVariant;
  action?: ToastAction;
  /** Auto-dismiss delay in ms; `0` keeps the toast until dismissed. Default 4000. */
  duration?: number;
}

interface Toast extends Required<Pick<ToastOptions, 'variant'>> {
  id: number;
  message: string;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// Toast recipe (DESIGN.md §3.18): tinted background, semantic left bar, ring.
const VARIANT_STYLES: Record<ToastVariant, string> = {
  success:
    'border-emerald-500 bg-emerald-50/90 text-emerald-800 ring-emerald-200/50 dark:bg-emerald-950/90 dark:text-emerald-200 dark:ring-emerald-900/50',
  error:
    'border-rose-500 bg-rose-50/90 text-rose-800 ring-rose-200/50 dark:bg-rose-950/90 dark:text-rose-200 dark:ring-rose-900/50',
  info: 'border-sky-500 bg-sky-50/90 text-sky-800 ring-sky-200/50 dark:bg-sky-950/90 dark:text-sky-200 dark:ring-sky-900/50',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, options?: ToastOptions): number => {
      const id = nextIdRef.current++;
      const variant = options?.variant ?? 'info';
      const duration = options?.duration ?? 4000;
      setToasts((prev) => [...prev, { id, message, variant, action: options?.action }]);
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext value={value}>
      {children}
      <div
        className="pointer-events-none fixed right-4 top-[4.5rem] z-[60] flex w-full max-w-sm flex-col gap-2"
        aria-live="polite"
        role="status"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-start gap-3 rounded-lg border-l-4 px-4 py-3 text-sm font-medium shadow-lg ring-1 backdrop-blur-sm ${VARIANT_STYLES[t.variant]}`}
          >
            <span className="flex-1">{t.message}</span>
            {t.action ? (
              <button
                type="button"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
                className="shrink-0 font-semibold underline underline-offset-2 hover:opacity-80"
              >
                {t.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Schließen"
              className="shrink-0 rounded-md p-1 opacity-60 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export function useToast(): ToastContextValue {
  const ctx = use(ToastContext);
  if (!ctx) {
    throw new Error('useToast muss innerhalb von <ToastProvider> verwendet werden.');
  }
  return ctx;
}
